import {
  isMultileg,
  ProductTypeEnum,
  productTypeLabel,
  SyntheticProductTypeEnum,
  type Currency,
  type Market,
  type Security,
} from '@talos/kyoko';
import { matchesPriceGridSourceFiltersWithoutProductTypes } from '../hooks/usePriceGridFilter';
import type { PriceGridFilterConfig, PriceGridFilterState, PriceGridSourceItem } from '../types';

export const GENERIC_PRICE_GRID_PRODUCT_TYPES = [
  ProductTypeEnum.Spot,
  ProductTypeEnum.PerpetualSwap,
  ProductTypeEnum.Future,
  ProductTypeEnum.CFD,
  ProductTypeEnum.CalendarSpread,
] as const;

export type PriceGridProductType = (typeof GENERIC_PRICE_GRID_PRODUCT_TYPES)[number];

export function isPriceGridProductType(productType: ProductTypeEnum): productType is PriceGridProductType {
  return GENERIC_PRICE_GRID_PRODUCT_TYPES.some(value => value === productType);
}

export function getGenericPriceGridProductTypeLabel(productType: PriceGridProductType) {
  if (productType === ProductTypeEnum.CalendarSpread) {
    return 'Cal Spread';
  }

  return productType === ProductTypeEnum.CFD ? 'Swap/CFD' : productTypeLabel(productType);
}

/**
 * Product-type strings to match against the Price Grid search input.
 *
 * Generic Price Grid filter buttons use shortened labels from {@link getGenericPriceGridProductTypeLabel}
 * (e.g. "Cal Spread", "Swap/CFD") while Kyoko's {@link productTypeLabel} may differ (e.g. "Calendar Spread", "Swap").
 * Search includes both so typing either the on-screen button text or the standard label finds the same rows.
 */
export function getPriceGridProductTypeSearchLabels(productType: ProductTypeEnum) {
  const labels = new Set<string>([productTypeLabel(productType)]);

  if (isPriceGridProductType(productType)) {
    labels.add(getGenericPriceGridProductTypeLabel(productType));
  }

  return [...labels];
}

export function getMultilegSyntheticProductTypeLabel(syntheticProductType: SyntheticProductTypeEnum | undefined) {
  switch (syntheticProductType) {
    case SyntheticProductTypeEnum.Cross:
      return 'Cross';
    case SyntheticProductTypeEnum.Delta1Spread:
      return 'Delta-1';
    case SyntheticProductTypeEnum.CallCalendarSpread:
    case SyntheticProductTypeEnum.PutCalendarSpread:
      return 'Cal Spread';
    case SyntheticProductTypeEnum.CallSpread:
    case SyntheticProductTypeEnum.PutSpread:
      return 'Ver Spread';
    default:
      return syntheticProductType ? productTypeLabel(syntheticProductType) : '';
  }
}

export function getPriceGridMultilegProductTypeSearchLabels(security: Security) {
  const syntheticProductType = security.MultilegDetails?.SyntheticProductType;
  return [
    getMultilegSyntheticProductTypeLabel(syntheticProductType),
    syntheticProductType ? productTypeLabel(syntheticProductType) : undefined,
  ].filter((label): label is string => label != null && label !== '');
}

export const GENERIC_PRICE_GRID_PRODUCT_TYPE_OPTIONS = GENERIC_PRICE_GRID_PRODUCT_TYPES.map(productType => ({
  productType,
  label: getGenericPriceGridProductTypeLabel(productType),
}));

export function getGenericPriceGridMarketOptions(markets: Market[]) {
  return markets.map(market => market.Name);
}

export function getGenericPriceGridCurrencyOptions(currencies: Currency[]) {
  return currencies.map(currency => currency.Symbol);
}

export function getGenericPriceGridBasePairKey({
  BaseCurrency: baseCurrency,
  QuoteCurrency: quoteCurrency,
}: Pick<Security, 'BaseCurrency' | 'QuoteCurrency'>) {
  return baseCurrency && quoteCurrency ? `${baseCurrency}-${quoteCurrency}` : undefined;
}

export function getGenericPriceGridBasePairOptions(securities: Security[]) {
  return [
    ...new Set(
      securities
        .filter(security => security.ProductType === ProductTypeEnum.Spot)
        .map(getGenericPriceGridBasePairKey)
        .filter((basePair): basePair is string => basePair != null)
    ),
  ];
}

export function genericPriceGridSourceFilter(sourceItem: PriceGridSourceItem, filterState: PriceGridFilterState) {
  const security = sourceItem.security;
  const selectedProductTypes = filterState.productTypes;
  const productTypes =
    selectedProductTypes && selectedProductTypes.length > 0 ? selectedProductTypes : GENERIC_PRICE_GRID_PRODUCT_TYPES;

  // Multileg securities use separate price-grid handling.
  if (isMultileg(security) || security.MultilegDetails != null) {
    return false;
  }

  // Generic Price Grid only supports the configured product type set.
  if (!isPriceGridProductType(security.ProductType)) {
    return false;
  }

  // Active product-type filters narrow the supported generic set.
  if (!productTypes.includes(security.ProductType)) {
    return false;
  }

  return matchesPriceGridSourceFiltersWithoutProductTypes(sourceItem, filterState);
}

export const GENERIC_PRICE_GRID_FILTER_CONFIG = {
  initialState: {},
  clientFilter: genericPriceGridSourceFilter,
} satisfies PriceGridFilterConfig<PriceGridSourceItem>;
